import React, { useEffect, useMemo, useState } from 'react';
import {
  BriefcaseBusiness, ChevronRight, CircleDollarSign, GripVertical, MapPin, Plus, RotateCcw,
  Save, Trash2, Upload, X,
} from 'lucide-react';
import { api } from '../api.js';
import MarkdownEditor from '../components/MarkdownEditor.jsx';
import { Badge, Button, Callout, IconButton, PageHeader, SectionHeader } from '../components/ui.jsx';
import { classNames } from '../utils.js';

const DEFAULT_DIMENSIONS = [
  { key: 'experience', label: '岗位相关经验', weight: 30 },
  { key: 'skills', label: '核心技能匹配', weight: 30 },
  { key: 'growth', label: '成长潜力', weight: 15 },
  { key: 'career', label: '职业路径匹配', weight: 15 },
  { key: 'team', label: '团队补位匹配', weight: 10 },
];
const EDIT_TABS = [
  ['basic', '基本信息'], ['requirements', '要求与技能'], ['team', '团队与目标'], ['weights', '评分权重'],
];

const blankJob = () => ({
  title: '', departmentId: '', department: '', level: 'Mid', salaryMin: '', salaryMax: '', salaryCurrency: 'CNY',
  location: '', jdText: '', hardRequirements: [], skillPriorities: [],
  teamContext: { roles: [], gaps: [], reportsTo: '', collaborators: '', businessStage: '' },
  hiringGoal: '', scoringDimensions: DEFAULT_DIMENSIONS.map((item) => ({ ...item })), status: 'active',
});

function normalizeDimensions(job = {}) {
  const source = job.scoringDimensions?.length
    ? job.scoringDimensions
    : Object.entries(job.weights || {}).map(([key, weight]) => ({
      key, label: DEFAULT_DIMENSIONS.find((item) => item.key === key)?.label || key, weight,
    }));
  const values = source.length ? source : DEFAULT_DIMENSIONS;
  const total = values.reduce((sum, item) => sum + Math.max(0, Number(item.weight || 0)), 0);
  if (!total) return DEFAULT_DIMENSIONS.map((item) => ({ ...item }));
  let assigned = 0;
  return values.map((item, index) => {
    const weight = index === values.length - 1
      ? Math.round((100 - assigned) * 10) / 10
      : Math.round(Math.max(0, Number(item.weight || 0)) / total * 1000) / 10;
    assigned += weight;
    return { key: item.key, label: item.label, weight };
  });
}

const normalizeJob = (job) => job ? {
  ...blankJob(), ...job,
  hardRequirements: job.hardRequirements || [],
  skillPriorities: (job.skillPriorities || []).map(normalizeSkill), teamContext: { ...blankJob().teamContext, ...(job.teamContext || {}) },
  scoringDimensions: normalizeDimensions(job),
} : blankJob();

const roundPercent = (value) => Math.round(Number(value || 0) * 10) / 10;

function parseListText(value) {
  return String(value || '').split(/[\r\n,，;；、]+/).map((item) => item.trim()).filter(Boolean);
}

function normalizeSkill(skill = {}) {
  const experienceAreas = skill.experienceAreas ?? skill.experienceRequirement ?? skill.minimumEvidence ?? '';
  const { minimumEvidence: _minimumEvidence, experienceRequirement: _experienceRequirement, ...rest } = skill;
  return { ...rest, experienceAreas: String(experienceAreas || '') };
}

function TextListInput({ value = [], onChange, placeholder, rows = 5 }) {
  const canonicalValue = value.join('\n');
  const [draft, setDraft] = useState(canonicalValue);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(canonicalValue);
  }, [canonicalValue, editing]);

  return (
    <textarea
      rows={rows}
      value={draft}
      onFocus={() => setEditing(true)}
      onChange={(event) => {
        setDraft(event.target.value);
        onChange(parseListText(event.target.value));
      }}
      onBlur={() => {
        setEditing(false);
        setDraft(parseListText(draft).join('\n'));
      }}
      placeholder={placeholder}
    />
  );
}

export default function JobsPage({ state, selectedJobId, refresh, notify }) {
  const initialJobId = selectedJobId || state?.jobs?.[0]?.id || '';
  const [editingId, setEditingId] = useState(initialJobId);
  const [form, setForm] = useState(() => normalizeJob(state?.jobs?.find((item) => item.id === initialJobId)));
  const [tab, setTab] = useState('basic');
  const [jdFile, setJdFile] = useState(null);
  const [formattingJd, setFormattingJd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const jobs = state?.jobs || [];
  const editingJobVersion = jobs.find((item) => item.id === editingId)?.updatedAt || '';

  useEffect(() => {
    if (!editingId && jobs[0]?.id) setEditingId(jobs[0].id);
  }, [jobs, editingId]);
  useEffect(() => {
    if (editingId === 'new') return;
    const job = jobs.find((item) => item.id === editingId);
    if (job) { setForm(normalizeJob(job)); setDirty(false); setJdFile(null); }
  }, [editingId, editingJobVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const weightTotal = useMemo(() => roundPercent(form.scoringDimensions.reduce((sum, item) => sum + Number(item.weight || 0), 0)), [form.scoringDimensions]);
  const skillWeightTotal = useMemo(() => roundPercent(form.skillPriorities.reduce((sum, item) => sum + Number(item.weight || 0), 0)), [form.skillPriorities]);
  const set = (key, value) => { setForm((current) => ({ ...current, [key]: value })); setDirty(true); };
  const setTeam = (key, value) => { setForm((current) => ({ ...current, teamContext: { ...current.teamContext, [key]: value } })); setDirty(true); };

  const chooseJob = (id) => {
    if (dirty && !window.confirm('当前岗位有未保存修改，确认切换吗？')) return;
    setEditingId(id); setTab('basic');
  };

  const createNew = () => {
    if (dirty && !window.confirm('当前岗位有未保存修改，确认新建吗？')) return;
    setEditingId('new'); setForm(blankJob()); setTab('basic'); setDirty(false); setJdFile(null);
  };

  const reset = () => {
    const original = editingId === 'new' ? blankJob() : normalizeJob(jobs.find((job) => job.id === editingId));
    setForm(original);
    setDirty(false);
    setJdFile(null);
    notify(editingId === 'new' ? '已清空新岗位表单' : '已恢复上次保存的岗位配置');
  };

  const save = async () => {
    if (!form.title.trim()) return notify('请填写岗位名称', 'danger');
    if (!form.departmentId) return notify('请选择所属部门', 'danger');
    if (Math.abs(weightTotal - 100) > 0.01) return notify(`评分权重合计需为 100%，当前为 ${weightTotal}%`, 'danger');
    setSaving(true);
    try {
      const payload = {
        ...form,
        weights: Object.fromEntries(form.scoringDimensions.map((item) => [item.key, Number(item.weight || 0)])),
        hardRequirements: (form.hardRequirements || []).map((item) => String(item).trim()).filter(Boolean),
        teamContext: {
          ...form.teamContext,
          roles: (form.teamContext.roles || []).map((item) => String(item).trim()).filter(Boolean),
          gaps: (form.teamContext.gaps || []).map((item) => String(item).trim()).filter(Boolean),
        },
      };
      const saved = await api.saveJobForm(editingId === 'new' ? null : editingId, payload, jdFile);
      setEditingId(saved.id); setForm(normalizeJob(saved)); setDirty(false); setJdFile(null);
      notify(editingId === 'new' ? '岗位已创建' : '岗位配置已保存');
      await refresh();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setSaving(false); }
  };

  const formatJd = async () => {
    if (!form.jdText.trim()) return notify('请先粘贴 JD 内容', 'danger');
    setFormattingJd(true);
    try {
      const result = await api.formatJobDescription(form.jdText);
      set('jdText', result.markdown);
      notify('JD 已按标准结构整理');
    } catch (error) { notify(error.message, 'danger'); }
    finally { setFormattingJd(false); }
  };

  const remove = async () => {
    if (editingId === 'new' || !window.confirm(`删除岗位“${form.title}”及其候选人数据？此操作不可撤销。`)) return;
    try { await api.deleteJob(editingId); notify('岗位已删除'); setEditingId(''); await refresh(); }
    catch (error) { notify(error.message, 'danger'); }
  };

  const updateSkill = (index, patch) => set('skillPriorities', form.skillPriorities.map((skill, skillIndex) => skillIndex === index ? { ...skill, ...patch } : skill));
  const updateDimension = (index, patch) => set('scoringDimensions', form.scoringDimensions.map((dimension, dimensionIndex) => dimensionIndex === index ? { ...dimension, ...patch } : dimension));
  const addDimension = () => set('scoringDimensions', [...form.scoringDimensions, {
    key: `dimension_${Date.now()}`, label: '新评分指标', weight: 0,
  }]);
  const removeDimension = (index) => {
    if (form.scoringDimensions.length <= 2) return notify('至少保留两个评分维度', 'danger');
    set('scoringDimensions', form.scoringDimensions.filter((_, dimensionIndex) => dimensionIndex !== index));
  };

  return (
    <div className="page jobs-page">
      <PageHeader eyebrow={`${jobs.length} 个岗位 · ${jobs.filter((job) => job.status === 'active').length} 个招聘中`} title="岗位配置" description="岗位证据门槛与评分权重决定候选人如何排序" actions={<Button variant="primary" icon={Plus} onClick={createNew}>新建岗位</Button>} />
      <div className="jobs-layout">
        <aside className="job-list-panel">
          <div className="job-list-head"><strong>全部岗位</strong><Badge>{jobs.length}</Badge></div>
          <div className="job-list">
            {jobs.map((job) => (
              <button key={job.id} className={classNames(editingId === job.id && 'active')} onClick={() => chooseJob(job.id)}>
                <span className="job-list-icon"><BriefcaseBusiness size={18} /></span>
                <div><strong>{job.title}</strong><span>{job.department || '未设置部门'} · {job.level}</span><small>{job.candidateCount} 位候选人 · {job.reviewCount} 人待复核</small></div>
                <ChevronRight size={16} />
              </button>
            ))}
          </div>
          <button className="new-job-list-button" onClick={createNew}><Plus size={16} />添加岗位</button>
        </aside>

        <section className="job-editor">
          <header className="job-editor-head">
            <div><span>{editingId === 'new' ? '新岗位' : '编辑岗位'}</span><h2>{form.title || '未命名岗位'}</h2></div>
            <div><Button icon={RotateCcw} onClick={reset}>重置</Button>{editingId !== 'new' && <Button variant="danger-ghost" icon={Trash2} onClick={remove}>删除</Button>}</div>
          </header>
          <nav className="editor-tabs">{EDIT_TABS.map(([id, label]) => <button key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}>{label}{id === 'weights' && <span className={weightTotal === 100 ? 'ok' : 'warn'}>{weightTotal}%</span>}</button>)}</nav>

          <div className="job-editor-body">
            {tab === 'basic' && (
              <div className="form-section">
                <SectionHeader title="岗位基本信息" description="用于定义筛选范围与候选人预期" />
                <div className="form-grid two">
                  <label><span>岗位名称 *</span><div className="input-with-icon"><BriefcaseBusiness size={16} /><input value={form.title} onChange={(event) => set('title', event.target.value)} placeholder="例如：增长型 UI/UX 设计师" /></div></label>
                  <label><span>所属部门 *</span><select value={form.departmentId} onChange={(event) => set('departmentId', event.target.value)}><option value="">请选择部门</option>{(state?.departments || []).filter((item) => item.active || item.id === form.departmentId).map((department) => <option key={department.id} value={department.id}>{department.name}{department.active ? '' : '（已停用）'}</option>)}</select></label>
                  <label><span>岗位等级</span><select value={form.level} onChange={(event) => set('level', event.target.value)}><option>Junior</option><option>Mid</option><option>Senior</option><option>Lead</option><option>Head</option></select></label>
                  <label><span>工作地点</span><div className="input-with-icon"><MapPin size={16} /><input value={form.location} onChange={(event) => set('location', event.target.value)} placeholder="杭州 / 新加坡" /></div></label>
                </div>
                <div className="form-grid salary-grid">
                  <label><span>最低薪资</span><div className="input-with-icon"><CircleDollarSign size={16} /><input type="number" value={form.salaryMin} onChange={(event) => set('salaryMin', event.target.value)} /></div></label>
                  <label><span>最高薪资</span><input type="number" value={form.salaryMax} onChange={(event) => set('salaryMax', event.target.value)} /></label>
                  <label><span>币种</span><select value={form.salaryCurrency} onChange={(event) => set('salaryCurrency', event.target.value)}><option>CNY</option><option>SGD</option><option>USD</option></select></label>
                </div>
                <label className="full-field"><span>岗位 JD</span><MarkdownEditor value={form.jdText} onChange={(value) => set('jdText', value)} onFormat={formatJd} formatting={formattingJd} /></label>
                <div className="jd-upload-row">
                  <label className="file-button"><Upload size={16} /><span>{jdFile ? jdFile.name : '上传 JD 文件'}</span><input type="file" accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" onChange={(event) => { setJdFile(event.target.files[0]); setDirty(true); }} /></label>
                  <span>{jdFile ? '保存后将解析文件、统一排版并替换编辑器中的内容' : '支持 PDF、DOCX、TXT 与图片；导入后会自动整理为标准结构'}</span>
                </div>
              </div>
            )}

            {tab === 'requirements' && (
              <div className="form-section">
                <SectionHeader title="硬门槛" description="不满足时强标记风险，但不自动淘汰" />
                <label className="full-field"><span>每行一项</span><TextListInput value={form.hardRequirements} onChange={(value) => set('hardRequirements', value)} placeholder={'至少 3 年相关经验\n提供可访问的作品集链接（仅人工查看）\n具备英文工作沟通能力'} /></label>
                <div className="skills-header"><SectionHeader title="技能优先级" description="描述该技能需要覆盖的业务领域或经验场景，不要求填写量化最低证据" /><Button size="sm" icon={Plus} onClick={() => set('skillPriorities', [...form.skillPriorities, { id: `skill_${Date.now()}`, name: '', type: 'core', weight: 10, experienceAreas: '' }])}>添加技能</Button></div>
                <div className="skills-table">
                  <div className="skills-table-head"><span>技能</span><span>类型</span><span>权重</span><span>领域经验要求</span><span /></div>
                  {form.skillPriorities.map((skill, index) => (
                    <div className="skill-row" key={skill.id || index}><GripVertical size={16} /><input value={skill.name} onChange={(event) => updateSkill(index, { name: event.target.value })} placeholder="技能名称" /><select value={skill.type} onChange={(event) => updateSkill(index, { type: event.target.value })}><option value="hard">硬门槛</option><option value="core">核心</option><option value="bonus">加分</option></select><label><input type="number" min="0" max="100" value={skill.weight} onChange={(event) => updateSkill(index, { weight: Number(event.target.value) })} /><span>%</span></label><input value={skill.experienceAreas ?? ''} onChange={(event) => updateSkill(index, { experienceAreas: event.target.value })} placeholder="例如：DTC 独立站、Marketplace 或 B2B 业务" /><IconButton icon={X} label="删除技能" onClick={() => set('skillPriorities', form.skillPriorities.filter((_, itemIndex) => itemIndex !== index))} /></div>
                  ))}
                  {form.skillPriorities.length === 0 && <div className="skills-empty">尚未添加技能优先级</div>}
                </div>
                <div className={`weight-total-line ${skillWeightTotal === 100 ? 'ok' : 'warn'}`}><span>技能权重合计</span><strong>{skillWeightTotal}%</strong><small>{skillWeightTotal === 100 ? '分配完整' : '建议合计为 100%'}</small></div>
              </div>
            )}

            {tab === 'team' && (
              <div className="form-section">
                <SectionHeader title="团队上下文" description="让评分考虑真实补位目标，而非只匹配 JD 关键词" />
                <div className="form-grid two">
                  <label><span>现有团队角色</span><TextListInput value={form.teamContext.roles} onChange={(value) => setTeam('roles', value)} placeholder={'产品负责人\n增长产品经理\n前端工程师'} /></label>
                  <label><span>团队能力缺口</span><TextListInput value={form.teamContext.gaps} onChange={(value) => setTeam('gaps', value)} placeholder={'电商转化方法\n系统化 UX\n跨语言体验'} /></label>
                  <label><span>汇报对象与汇报关系</span><textarea rows="3" value={form.teamContext.reportsTo} onChange={(event) => setTeam('reportsTo', event.target.value)} placeholder="可分行说明直线和虚线汇报关系" /></label>
                  <label><span>主要协作对象</span><textarea rows="3" value={form.teamContext.collaborators} onChange={(event) => setTeam('collaborators', event.target.value)} placeholder="可分行说明内部与外部协作对象" /></label>
                </div>
                <label className="full-field"><span>当前业务阶段</span><textarea rows="4" value={form.teamContext.businessStage} onChange={(event) => setTeam('businessStage', event.target.value)} placeholder="例如：品牌全球化与独立站转化提升期，可分行补充当前问题与优先级" /></label>
                <label className="full-field"><span>本次招聘补位目标</span><textarea rows="6" value={form.hiringGoal} onChange={(event) => set('hiringGoal', event.target.value)} placeholder="说明这个人加入后需要改变什么，而不只是完成什么任务" /></label>
              </div>
            )}

            {tab === 'weights' && (
              <div className="form-section weights-section">
                <SectionHeader title="岗位评分权重" description="每个岗位可独立维护评分指标；普通岗位不建议把语言作为评分项" action={<Button size="sm" icon={Plus} onClick={addDimension}>添加指标</Button>} />
                <Callout tone={weightTotal === 100 ? 'success' : 'warning'} title={`当前合计 ${weightTotal}%`}>{weightTotal === 100 ? '权重分配完整，可以保存。' : '所有维度权重必须合计为 100%。'}</Callout>
                <div className="weight-editor">
                  {form.scoringDimensions.map((dimension, index) => (
                    <div key={dimension.key}><span><GripVertical size={15} /><input aria-label={`评分指标 ${index + 1}`} value={dimension.label} onChange={(event) => updateDimension(index, { label: event.target.value })} /></span><div className="weight-slider-track"><i style={{ width: `${dimension.weight}%` }} /></div><label><input type="number" min="0" max="100" value={dimension.weight} onChange={(event) => updateDimension(index, { weight: Number(event.target.value) })} /><span>%</span></label><IconButton icon={Trash2} label={`删除${dimension.label}`} onClick={() => removeDimension(index)} /></div>
                  ))}
                </div>
                <div className="weight-visual">{form.scoringDimensions.filter((item) => item.weight > 0).map((item) => <i key={item.key} style={{ width: `${item.weight}%` }} title={`${item.label} ${item.weight}%`}><span>{item.weight >= 10 ? item.label : ''}</span></i>)}</div>
              </div>
            )}
          </div>
          <footer className="job-editor-footer"><span>{dirty ? '有未保存修改' : editingId === 'new' ? '尚未保存的新岗位' : `已保存 · ${form.updatedAt ? new Date(form.updatedAt).toLocaleString('zh-CN') : ''}`}</span><div><Button variant="primary" icon={Save} loading={saving} onClick={save}>保存配置</Button></div></footer>
        </section>
      </div>
    </div>
  );
}
